import { tFunctionType } from 'app/services/localization';
import createErrorType from 'app/utils/createErrorType';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import * as iots from 'io-ts';
import { AuthProvider, FACEBOOK } from 'app/constants/enums/AuthProvider';

// TODO: are these complete?
enum Codes {
  INVALID_CLIENT       = 'invalid_client',
  INVALID_REDIRECT_URI = 'invalid_redirect_uri', // TODO: what is this? not in any standard
  INVALID_GRANT        = 'invalid_grant',
  INVALID_SCOPE        = 'invalid_scope',
  INVALID_REQUEST      = 'invalid_request',
  SERVER_ERROR         = 'server_error',
  ACCESS_DENIED        = 'access_denied',
  BLOCKED              = 'blocked',  // HelloJS auth popup blocked

  // TODO:
  // unauthorized_client
  // unsupported_grant_type
  // unsupported_response_type
  // access_denied
  // temporarily_unavailable
}

enum ExtendedCodes {
  PASSWORD_INCORRECT    = 'password_incorrect',
  PASSWORD_INSUFFICIENT = 'password_insufficient',
  USER_INVALID          = 'user_invalid',
  USER_UNCONFIRMED      = 'user_unconfirmed',
  USER_LOCKED           = 'user_locked',
  INVALID_CUSTOM_PARAMS = 'invalid_custom_params',
  STATE_MISMATCH        = 'state_mismatch',
  EMAIL_TAKEN           = 'email_taken',
  UID_TAKEN             = 'uid_taken',
  EXTERNAL_REJECTION    = 'external_rejection',
  EXTERNAL_UNAVAILABLE  = 'external_unavailable',
}

export const isFacebookError = (error: unknown) => (
  error instanceof OauthError &&
  (error.oauthError.error === OauthError.Codes.SERVER_ERROR ||
    error.oauthError.error === OauthError.Codes.ACCESS_DENIED)
  && error.provider === FACEBOOK
);

export const isEmailTakenError = (error: unknown) => (
  error instanceof OauthError &&
  error.oauthError.extended_error === OauthError.ExtendedCodes.EMAIL_TAKEN
);

class OauthError extends createErrorType('OauthError') {
  public static readonly Codes = Codes;
  public static readonly ExtendedCodes = ExtendedCodes;

  public static readonly schema = iots.intersection([
    iots.interface({
      error: iots.string,
    }),
    iots.partial({
      redirect_uri:      iots.string,
      error_description: iots.string,
      error_uri:         iots.string,
      extended_error:    iots.string,
      meta:              iots.string,
    }),
  ]);

  public static formatOauthError(oauthError: iots.TypeOf<typeof OauthError.schema>) {
    return `OauthError(${oauthError.error}): ${oauthError.error_description}`;
  }

  public static tryParse(
    x: any,
    { cause, provider }: { cause?: any, provider?: AuthProvider } = {},
  ): OauthError | null {
    return pipe(
      OauthError.schema.decode(x),
      fold(
        () => {
          return null;
        },
        (oauthError) => {
          return new OauthError(oauthError, cause, provider);
        },
      ),
    );
  }

  private constructor(
    public readonly oauthError: iots.TypeOf<typeof OauthError.schema>,
    public readonly cause?: any,
    public readonly provider?: AuthProvider,
  ) {
    super(OauthError.formatOauthError(oauthError));
  }

  public get code() {
    return this.oauthError.error;
  }

  public get extendedCode() {
    return this.oauthError.extended_error;
  }

  public localizedErrorMessage(t: tFunctionType) {
    switch (this.extendedCode) {
      case OauthError.ExtendedCodes.PASSWORD_INCORRECT:
        return t('auth.error.password-incorrect');
      case OauthError.ExtendedCodes.PASSWORD_INSUFFICIENT:
        return t('auth.error.password-insufficient');
      case OauthError.ExtendedCodes.USER_INVALID:
        return t('auth.error.user-invalid');
      case OauthError.ExtendedCodes.USER_UNCONFIRMED:
        return t('auth.error.user-unconfirmed');
      case OauthError.ExtendedCodes.USER_LOCKED:
        return t('auth.error.user-locked');
      case OauthError.ExtendedCodes.INVALID_CUSTOM_PARAMS:
        return t('auth.error.invalid-custom-params');
      case OauthError.ExtendedCodes.STATE_MISMATCH:
        return t('auth.error.state-mismatch');
      case OauthError.ExtendedCodes.EMAIL_TAKEN:
        return t('auth.error.email-taken');
      case OauthError.ExtendedCodes.EXTERNAL_REJECTION:
        return t('auth.error.external-rejection', {
          provider: this.provider?.getName(t),
        });
      case OauthError.ExtendedCodes.EXTERNAL_UNAVAILABLE:
        return t('auth.error.external-unavailable', {
          provider: this.provider?.getName(t),
        });
      default:
        return null;
    }

    // TODO: do generic error messages have useful translations?
  }
}

export default OauthError;
